import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';

import intlShape from '@/shared/utils/intlShape';
import SimplePanel from '@/shared/components/SimplePanel';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import api from '@/shared/api';
import config from '@/config';
import { injectStateService, StateService } from '@/shared/services/StateService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import Checkbox from '@/shared/components/input/Checkbox';
import Button from '@/shared/components/Button';
import handleHttpError from '@/shared/services/HandleHttpError';
import { AddIcon } from '@/shared/components/Icons';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import YesNoModal from '@/shared/components/YesNoModal';
import TrainingModal from './TrainingModal';
import TrainingEmployeeModal from './TrainingEmployeeModal';
import TrainingOverviewRow from './TrainingOverviewRow';
import messages from './trainingMessages';
import TrainingOverviewFilters, { defaultFilters } from './TrainingOverviewFilters';

const SessionStorageKey = 'TrainingOverviewParams';

class TrainingOverviewContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;
    const { trainingFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionFilters.dateFrom) {
      sessionFilters.dateFrom = moment.utc(sessionFilters.dateFrom);
    }
    if (sessionFilters.dateUntil) {
      sessionFilters.dateUntil = moment.utc(sessionFilters.dateUntil);
    }

    this.state = {
      items: [],
      currentSearchParams: undefined,
      allSelected: false,
      initialSearchParams: {
        // Set default filters
        sortKey: 'name',
        pageSize: -1,
        trainingFilters: {
          ...defaultFilters,
          ...sessionFilters,
          ...searchParams,
        },
        // Override with session storage params
        ...sessionParams,
      },
      showTrainingModal: false,
      selectedTraining: null,
    };
  }

  componentDidMount() {}

  componentWillUnmount() {
    if (this.trainingCancelToken && !this.trainingCancelToken.token.reason) {
      this.trainingCancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadItems = searchParams => {
    sessionStorage.setItem(
      SessionStorageKey,
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        trainingFilters: searchParams.trainingFilters,
      }),
    );
    const { allSelected } = this.state;
    const { intl } = this.props;
    if (this.trainingCancelToken && !this.trainingCancelToken.token.reason) {
      this.trainingCancelToken.cancel('Operation canceled due to new request.');
    }
    this.trainingCancelToken = Axios.CancelToken.source();

    const { period, ...filtersWithoutPeriod } = searchParams.trainingFilters;

    const cleanedFilters = {
      ...filtersWithoutPeriod,
      dateFrom: filtersWithoutPeriod.dateFrom
        ? searchParams.trainingFilters.dateFrom.toISOString()
        : undefined,
      dateUntil: filtersWithoutPeriod.dateUntil
        ? searchParams.trainingFilters.dateUntil.toISOString()
        : undefined,
      trainingStatuses: searchParams.trainingFilters.trainingStatuses || [],
    };

    const query = new URLSearchParams({
      desc: searchParams.desc,
      sort: searchParams.sortKey,
    }).toString();

    return api.base
      .post(`${config.appUrls.api}/api/Training/List?${query}`, cleanedFilters, {
        cancelToken: this.trainingCancelToken.token,
      })
      .then(({ data }) => {
        // If checkbox for all trainings is selected, set selected on all new incoming trainings
        const items = data.data.map(e => ({ ...e, selected: !!allSelected }));
        this.setState(state => ({
          ...state,
          items,
          currentSearchParams: searchParams,
        }));

        return items;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
        // Return empty array for cancelled requests so ScrollableTable can handle it properly
        return [];
      });
  };

  filtersChanged = filters => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        trainingFilters: filters,
      },
    }));
  };

  handleSelect = row => {
    this.setState(({ items }) => ({
      items: items.map(x => (x.id === row.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  handleSelectAll = () => {
    this.setState(({ items, allSelected }) => ({
      items: items.map(x => ({ ...x, selected: !allSelected })),
      allSelected: !allSelected,
    }));
  };

  onTrainingSaved = training => {
    const { items } = this.state;
    const existingIndex = items.findIndex(item => item.id === training.id);

    let updatedItems;
    if (existingIndex !== -1) {
      updatedItems = [...items];
      updatedItems[existingIndex] = training;
    } else {
      updatedItems = [training, ...items];
    }

    this.setState(state => ({
      ...state,
      showTrainingModal: false,
      selectedTraining: null,
      items: updatedItems,
    }));
  };

  onEditTrainingClick = training => {
    const trainingWithResourceId = {
      ...training,
      resourceId: training.resource?.id || null,
    };

    this.setState({
      selectedTraining: trainingWithResourceId,
      showTrainingModal: true,
    });
  };

  onUpdateTrainingEmployeesClick = (training, onRefresh) => {
    this.pendingRefresh = onRefresh;
    this.setState({
      showUpdateTrainingEmployeesModal: true,
      selectedTraining: training,
    });
  };

  updateTrainingEmployees = () => {
    if (this.pendingRefresh) {
      this.pendingRefresh();
      this.pendingRefresh = null;
    }
    this.setState({
      showUpdateTrainingEmployeesModal: false,
      selectedTraining: null,
    });
  };

  onDeleteTrainingClick = training => {
    this.setState(state => ({
      showDeleteTrainingModal: true,
      selectedTraining: training,
    }));
  };

  deleteTraining = () => {
    const { selectedTraining, items } = this.state;
    const { intl } = this.props;

    return api.base
      .delete(`${config.appUrls.api}/api/training/${selectedTraining.id}`)
      .then(() => {
        const updatedItems = items.filter(item => item.id !== selectedTraining.id);
        this.setState({
          items: updatedItems,
          showDeleteTrainingModal: false,
          selectedTraining: null,
        });
      })
      .catch(error => {
        handleHttpError(error, intl);
        this.setState({ showDeleteTrainingModal: false });
      });
  };

  render() {
    const { intl, stateService } = this.props;
    const {
      items,
      allSelected,
      initialSearchParams,
      showTrainingModal,
      showDeleteTrainingModal,
      showUpdateTrainingEmployeesModal,
      selectedTraining,
    } = this.state;

    return (
      <div className="training-overview">
        <SimplePanel
          article="20489529278748"
          title={intl.formatMessage(messages.TrainingsManagement)}
          buttons={
            <Button
              btnClassName="btn btn-xs btn-inverse margin10R"
              onClick={() =>
                this.setState({
                  showTrainingModal: true,
                  selectedTraining: null,
                })
              }
            >
              <span className="margin5R">
                <AddIcon />
              </span>
              {intl.formatMessage(messages.AddTraining)}
            </Button>
          }
        >
          <div className="info-block container-fluid">
            <TrainingOverviewFilters
              filters={initialSearchParams.trainingFilters}
              onFilterChange={this.filtersChanged}
              intl={intl}
            />
          </div>
        </SimplePanel>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => (
            <TrainingOverviewRow
              key={item.id}
              training={item}
              intl={intl}
              handleSelect={this.handleSelect}
              onEditTrainingClick={this.onEditTrainingClick}
              onDeleteTrainingClick={this.onDeleteTrainingClick}
              onUpdateTrainingEmployeesClick={this.onUpdateTrainingEmployeesClick}
              stateService={stateService}
            />
          )}
          noDataLabel={intl.formatMessage(messages.NoDataFound)}
        >
          <ColumnHeader
            className="checkbox-column"
            title={
              <Checkbox extraClass="margin2L" onClick={this.handleSelectAll} value={allSelected} />
            }
          />
          <ColumnHeader title={intl.formatMessage(messages.Name)} />
          <ColumnHeader title={intl.formatMessage(messages.Description)} />
          <ColumnHeader title={intl.formatMessage(messages.What)} />
          <ColumnHeader title={intl.formatMessage(messages.Status)} />
          <ColumnHeader title={intl.formatMessage(messages.Type)} />
          <ColumnHeader title={intl.formatMessage(messages.ExpectedTrainingHours)} />
          <ColumnHeader title={intl.formatMessage(messages.StartDate)} />
          <ColumnHeader title={intl.formatMessage(messages.EndDate)} />
          <ColumnHeader title={intl.formatMessage(messages.Mandatory)} />
          <ColumnHeader title={intl.formatMessage(messages.CertificateRequired)} />
          <ColumnHeader title={intl.formatMessage(messages.Progress)} />
          <ColumnHeader title={intl.formatMessage(messages.Actions)} className="text-right" />
        </ScrollableTable>
        {showTrainingModal && (
          <TrainingModal
            show
            training={selectedTraining}
            isEdit={!!selectedTraining}
            onSave={this.onTrainingSaved}
            onClose={() =>
              this.setState({
                showTrainingModal: false,
                selectedTraining: null,
              })
            }
          />
        )}
        {showDeleteTrainingModal && (
          <YesNoModal
            intl={intl}
            show={showDeleteTrainingModal}
            title={intl.formatMessage(messages.DeleteTraining)}
            message={intl.formatMessage(messages.DeleteTrainingConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                showDeleteTrainingModal: false,
                selectedTraining: null,
              }))
            }
            onSave={this.deleteTraining}
          />
        )}
        {showUpdateTrainingEmployeesModal && (
          <TrainingEmployeeModal
            show={showUpdateTrainingEmployeesModal}
            training={selectedTraining}
            onClose={() => {
              if (this.pendingRefresh) {
                this.pendingRefresh();
                this.pendingRefresh = null;
              }
              this.setState({
                showUpdateTrainingEmployeesModal: false,
                selectedTraining: null,
              });
            }}
            onSave={this.updateTrainingEmployees}
          />
        )}
      </div>
    );
  }
}

TrainingOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  searchParams: PropTypes.shape({}),
};

TrainingOverviewContainer.defaultProps = {
  searchParams: {},
};

export default injectStateService(injectIntl(TrainingOverviewContainer));
